import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Logo } from "../components/Logo";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        if (polls.current >= MAX_POLLS) {
          setState("pending");
          return;
        }
        polls.current += 1;
        timer = setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-silver-100/60 px-5" data-testid="payment-success-page">
      <Link to="/" className="mb-10">
        <Logo />
      </Link>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-silver-300/60 bg-white p-9 text-center shadow-[0_20px_60px_rgb(0,0,0,0.08)]"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-charcoal">{`Confirming your payment…`}</h1>
            <p className="mt-2 text-sm text-silver-500">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-light">
              <CheckCircle2 className="h-8 w-8 text-brand-dark" />
            </span>
            <h1 className="mt-5 font-display text-3xl font-black tracking-tight text-charcoal">{`You're all set! 🎉`}</h1>
            <p className="mt-2 text-sm text-silver-500">
              Your {info?.package_id ? `${info.package_id} ` : ""}subscription is active. Our team will reach out to
              set up your AI agent.
            </p>
            <Link to="/">
              <Button data-testid="success-home-btn" className="group mt-7 h-12 w-full bg-brand font-bold text-white hover:bg-brand-hover">
                Back to home <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-silver-500" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-charcoal">Payment processing</h1>
            <p className="mt-2 text-sm text-silver-500">
              This is taking longer than usual. {`We'll`} email your confirmation shortly.
            </p>
            <Link to="/">
              <Button variant="outline" className="mt-7 h-12 w-full font-bold">Back to home</Button>
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <XCircle className="h-8 w-8 text-red-500" />
            </span>
            <h1 className="mt-5 font-display text-2xl font-extrabold text-charcoal">Something went wrong</h1>
            <p className="mt-2 text-sm text-silver-500">{`We couldn't confirm this payment. Please try again.`}</p>
            <Link to="/pricing">
              <Button data-testid="success-retry-btn" className="mt-7 h-12 w-full bg-charcoal font-bold text-white hover:bg-charcoal/90">
                Back to pricing
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
